// ContainersPage/ContainersPage.js
import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import ContainerScannerModal from './ContainerScannerModal/ContainerScannerModal';
import styles from './ContainersPage.module.css';

const CONTAINER_PRESETS = [
  {
    label: 'Поддон 19л',
    type: 'poddon',
    bottle_volume_l: 19,
    capacity_total: 36,
    layout: { rows: [12, 12, 12], description: '3 ряда по 12 шт' },
    container_gtin: '04600840744001', // пример GTIN для 19 л
  },
  {
    label: 'Паллет 5л',
    type: 'pallet',
    bottle_volume_l: 5,
    capacity_total: 126,
    layout: { rows: [42, 42, 42], description: '3 ряда по 42 шт' },
    container_gtin: '04600840744070', // пример GTIN для 5 л
  },
  {
    label: 'Паллет 5л Холм',
    type: 'pallet',
    bottle_volume_l: 5,
    capacity_total: 126,
    layout: { rows: [42, 42, 42], description: '3 ряда по 42 шт' },
    container_gtin: '04600840744018', // пример GTIN для 5 л
  },
  {
    label: 'Паллет 6л',
    type: 'pallet',
    bottle_volume_l: 6,
    capacity_total: 100,
    layout: { rows: [24, 24, 24, 4], description: '3×24 + 4 сверху' },
    container_gtin: '04600840744032', // пример GTIN для 6 л
  },
  {
    label: 'Паллет 10л',
    type: 'pallet',
    bottle_volume_l: 10,
    capacity_total: 48,
    layout: { rows: [24, 24], description: '2 ряда по 24 шт' },
    container_gtin: '04600840744049', // пример GTIN для 10 л
  },
  {
    label: 'Паллет 5л Аква',
    type: 'pallet',
    bottle_volume_l: 5,
    capacity_total: 126,
    layout: { rows: [42, 42, 42], description: '3 ряда по 42 шт' },
    container_gtin: '04615985969028', // пример GTIN для 5 л

  },
  {
    label: 'Паллет 10л Аква',
    type: 'pallet',
    bottle_volume_l: 10,
    capacity_total: 48,
    layout: { rows: [24, 24], description: '2 ряда по 24 шт' },
    container_gtin: '04615985969004', // пример GTIN для 10 л
  },
];


export default function ContainersPage() {
  const [date, setDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scannerOpenFor, setScannerOpenFor] = useState(null);

  // загрузка списка контейнеров по дате
  const fetchContainers = async (fetchDate) => {
    setLoading(true);
    try {
      const data = await api.get('/containers', {
        params: { date: fetchDate },
      });
      setContainers(data);
    } catch (err) {
      console.error('Не удалось загрузить контейнеры', err);
    } finally {
      setLoading(false);
    }
  };

  // при старте и при изменении даты
  useEffect(() => {
    fetchContainers(date);
  }, [date]);

  // создание нового контейнера по пресету
  const createContainer = async (preset) => {
    try {
      await api.post('/containers', {
        code: `${preset.type}-${preset.bottle_volume_l}-${Date.now()}`,
        type: preset.type,
        bottle_volume_l: preset.bottle_volume_l,
        capacity_total: preset.capacity_total,
        layout: preset.layout,
        container_gtin: preset.container_gtin,
      });
      fetchContainers(date);
    } catch (err) {
      console.error('Не удалось создать контейнер', err);
    }
  };

  // удаление контейнера
  const deleteContainer = async (id, cascade = false) => {
    const вопрос = cascade
      ? 'Удалить контейнер и все марки в нём?'
      : 'Удалить контейнер?';
    if (!window.confirm(вопрос)) return;

    try {
      if (cascade) {
        await api.delete(`/containers/${id}/cascade`);
      } else {
        await api.delete(`/containers/${id}`);
      }
      fetchContainers(date);
    } catch (err) {
      console.error('Не удалось удалить контейнер', err);
      alert(err.response?.data?.error || 'Ошибка при удалении');
    }
  };

  const downloadWatermarkCSVForContainer = async (container) => {
    try {
      // Запрашиваем полные данные контейнера
      const data = await api.get(`/containers/${container.id}`);
      const marks = data.marks || [];
      let csv = '';
      const CRLF = '\r\n';

      marks.forEach(({ mark_code }) => {
        if (!mark_code) return;
        let cleaned = mark_code;
        // убираем GS (ASCII 29)
        if (cleaned.charCodeAt(0) === 29) {
          cleaned = cleaned.slice(1);
        }
        csv += cleaned + CRLF;
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const fileName = `watermark_${container.code}_${date}.csv`.replace(/[\s"]/g, '_');
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Не удалось скачать CSV', err);
      alert('Ошибка при получении данных контейнера');
    }
  };


  // открыть/закрыть сканер
  const openScanner = (container) => setScannerOpenFor(container);
  const closeScanner = () => setScannerOpenFor(null);

  // после скана — обновить список
  const onScanSuccess = () => fetchContainers(date);

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className={styles.datePicker}
        />
        <div className={styles.actions}>
          {CONTAINER_PRESETS.map((preset) => (
            <button
              key={preset.label}
              onClick={() => createContainer(preset)}
              className={styles.button}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </header>

      <main className={styles.content}>
        {loading ? (
          <p className={styles.loading}>Загрузка…</p>
        ) : (
          <ul className={styles.list}>
            {containers.map((c) => (
              <li
                  key={c.id}
                  className={`${styles.item} ${
                    c.currentCount === c.capacity_total ? styles.fullContainer : ''
                  }`}
                >
                <div className={styles.itemRow}>
                  <span className={styles.label}>Код:</span>
                  <span className={styles.value}>{c.code}</span>
                </div>
                <div className={styles.itemRow}>
                  <span className={styles.label}>Тип:</span>
                  <span className={styles.value}>
                    {c.type === 'pallet' ? 'Паллет' : 'Поддон'}
                  </span>
                </div>
                <div className={styles.itemRow}>
                  <span className={styles.label}>Литраж:</span>
                  <span className={styles.value}>{c.bottle_volume_l} л</span>
                </div>
                <div className={styles.itemRow}>
                  <span className={styles.label}>Вместимость:</span>
                  <span className={styles.value}>{c.capacity_total}</span>
                </div>
                <div className={styles.itemRow}>
                  <span className={styles.label}>Штук:</span>
                  <span className={styles.value}>{c.currentCount ?? 0}</span>
                </div>
                <div className={styles.itemButtons}>
                  <button
                    onClick={() => openScanner(c)}
                    className={styles.smallButton}
                  >
                    Сканировать
                  </button>
                  <button
                    onClick={() => deleteContainer(c.id, false)}
                    className={styles.smallButton}
                  >
                    Удалить
                  </button>
                  <button
                    onClick={() => downloadWatermarkCSVForContainer(c)}
                    className={styles.smallButton}
                  >
                    CSV
                  </button>

                  {/*<button
                    onClick={() => deleteContainer(c.id, true)}
                    className={styles.smallButton}
                  >
                    Удалить всё
                  </button>*/}
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>

      {scannerOpenFor && (
        <ContainerScannerModal
          container={scannerOpenFor}
          containerId={scannerOpenFor.id}
          isOpen={scannerOpenFor !== null}
          onClose={closeScanner}
          onScanSuccess={onScanSuccess}
        />
      )}
    </div>
  );
}
